import cls from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  useCheckEmailMutation,
  useSocialAuthMutation,
} from '@/entities/session';
import { AddEmailForm, SignUpEmailForm } from '@/features/auth/sign-up';
import { SocialButtons, useSocialLogin } from '@/features/auth/social-login';
import { parseApiError } from '@/shared/api/apiError';
import { AuthFrame } from '@/widgets/auth-frame';

import s from '@/shared/ui/AuthForm.module.css';

export function SignUpPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [checkEmail, checkEmailState] = useCheckEmailMutation();
  const [socialAuth, socialAuthState] = useSocialAuthMutation();
  const currentError =
    parseApiError(checkEmailState.error) ||
    parseApiError(socialAuthState.error);

  // Receives the values of the sign-up form or of the add-email form
  async function handleSubmit(data: { email: string }) {
    try {
      await checkEmail(data).unwrap();
      navigate('/check-mailbox', { state: data });
    } catch (_error) {}
  }

  const { loginWithGoogle, loginWithFacebook, handleAppleResponse } =
    useSocialLogin((socialType, accessToken, user) =>
      socialAuth({ access_token: accessToken, socialType, user }),
    );

  useEffect(() => {
    document.title = 'Sign Up - In_Home';
  }, []);

  return (
    <AuthFrame
      spinning={checkEmailState.isLoading || socialAuthState.isLoading}
    >
      <div className={s.content}>
        <div className={s.centered}>
          {pathname === '/add-email' ? (
            <div className={cls(s.card, s.cardShort)}>
              <h2 className={s.cardTitle}>{t('Enter Your Email')}</h2>
              <AddEmailForm onSubmit={handleSubmit} error={currentError} />
            </div>
          ) : (
            <div className={s.card}>
              <h2 className={s.cardTitle}>{t('Create an Account')}</h2>
              <SignUpEmailForm onSubmit={handleSubmit} error={currentError}>
                <div className={s.socialTitle}>
                  <div className={s.socialLine} />
                  <span>{t('Sign Up With')}</span>
                  <div className={s.socialLine} />
                </div>
                <SocialButtons
                  onGoogle={loginWithGoogle}
                  onFacebook={loginWithFacebook}
                  onApple={handleAppleResponse}
                  redirectPath="/sign-up"
                />
                <div className={s.footerLink}>
                  {t('Already have an account?')}{' '}
                  <Link to="/login">{t('Sign In')}</Link>
                </div>
              </SignUpEmailForm>
            </div>
          )}
        </div>
      </div>
    </AuthFrame>
  );
}
